import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, QrCode, Check, AlertTriangle } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { updateConfiguracaoClubeInput, LIMITES_BR_CODE } from '@portal-leo/shared';
import type { z } from 'zod';

type FormValues = z.infer<typeof updateConfiguracaoClubeInput>;

export function ConfiguracoesPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN');

  const utils = trpc.useUtils();
  const { data: configuracao, isLoading } = trpc.configuracao.get.useQuery();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(updateConfiguracaoClubeInput),
    defaultValues: {
      nomeClube: '',
      cnpj: '',
      pixChave: '',
      pixNomeRecebedor: '',
      pixCidade: '',
    },
  });

  const updateMutation = trpc.configuracao.update.useMutation({
    onSuccess: (salva) => {
      // Marca o formulário como limpo com os valores gravados. Sem isso ele
      // ficaria "sujo" para sempre e a guarda do efeito abaixo nunca mais
      // deixaria o formulário sincronizar.
      reset({
        nomeClube: salva.nomeClube,
        cnpj: salva.cnpj,
        pixChave: salva.pixChave,
        pixNomeRecebedor: salva.pixNomeRecebedor,
        pixCidade: salva.pixCidade,
      });
      utils.configuracao.get.invalidate();
    },
  });

  /**
   * Só sincroniza o formulário enquanto ele estiver intocado.
   *
   * Sem essa guarda, um refetch em segundo plano (o react-query refaz a
   * consulta ao voltar o foco da janela) sobrescreveria o que o administrador
   * está digitando.
   */
  useEffect(() => {
    if (!configuracao || isDirty) return;
    reset({
      nomeClube: configuracao.nomeClube,
      cnpj: configuracao.cnpj,
      pixChave: configuracao.pixChave,
      pixNomeRecebedor: configuracao.pixNomeRecebedor,
      pixCidade: configuracao.pixCidade,
    });
  }, [configuracao, isDirty, reset]);

  const pixIncompleto =
    configuracao &&
    (!configuracao.pixChave || !configuracao.pixNomeRecebedor || !configuracao.pixCidade);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="border-primary-600 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Configurações do clube</h1>

      <form onSubmit={handleSubmit((dados) => updateMutation.mutate(dados))} className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Identificação</h2>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Nome do clube"
              disabled={!canEdit}
              {...register('nomeClube')}
              error={errors.nomeClube?.message}
            />
            <Input
              label="CNPJ"
              placeholder="00.000.000/0000-00"
              disabled={!canEdit}
              {...register('cnpj')}
              error={errors.cnpj?.message}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Recebimento por PIX</h2>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Estes três campos montam o QR Code de pagamento do PDV. Preenchidos errado, o banco do
              cliente recusa a cobrança.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {pixIncompleto && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  Enquanto estes campos não estiverem completos, a finalização por PIX no PDV não
                  consegue gerar o QR Code.
                </span>
              </div>
            )}

            <Input
              label="Chave PIX"
              placeholder="CNPJ, e-mail, telefone ou chave aleatória"
              disabled={!canEdit}
              {...register('pixChave')}
              error={errors.pixChave?.message}
            />
            <Input
              label={`Nome do recebedor (até ${LIMITES_BR_CODE.nomeRecebedor} caracteres)`}
              placeholder="LEO CLUBE"
              disabled={!canEdit}
              {...register('pixNomeRecebedor')}
              error={errors.pixNomeRecebedor?.message}
            />
            <Input
              label={`Cidade do recebedor (até ${LIMITES_BR_CODE.cidade} caracteres)`}
              placeholder="SAO PAULO"
              disabled={!canEdit}
              {...register('pixCidade')}
              error={errors.pixCidade?.message}
            />
            <p className="text-xs text-gray-500">
              Acentos são removidos automaticamente ao gerar o QR — o padrão do Banco Central só
              aceita ASCII nestes dois campos.
            </p>
          </CardContent>
        </Card>

        {updateMutation.isError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{updateMutation.error.message}</span>
          </div>
        )}

        {updateMutation.isSuccess && !isDirty && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
            <Check className="h-4 w-4" />
            Configurações salvas.
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={!canEdit} isLoading={updateMutation.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ConfiguracoesPage;
